import React from 'react';
import { Package, Power, PowerOff, DollarSign, MapPin, Building, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import type { Ingredient } from '@/types/ingredient.types';

interface IngredientsTableProps {
  ingredients: Ingredient[];
  onEdit: (ingredient: Ingredient) => void;
  onToggleActive: (ingredient: Ingredient) => void;
  onManagePrices: (ingredient: Ingredient) => void;
  onManageLocations: (ingredient: Ingredient) => void;
  onManageSuppliers: (ingredient: Ingredient) => void;
}

type SortField = 'name' | 'category' | 'unit' | 'isActive';

export const IngredientsTable: React.FC<IngredientsTableProps> = ({
  ingredients,
  onEdit,
  onToggleActive,
  onManagePrices,
  onManageLocations,
  onManageSuppliers
}) => {
  // Configuración del hook reutilizable
  const fieldLabels: Record<SortField, string> = {
    name: 'Insumo',
    category: 'Categoría',
    unit: 'Unidad de medida',
    isActive: 'Estado'
  };

  // Extractor personalizado para campos complejos
  const sortValueExtractor = (ingredient: Ingredient, field: SortField): any => {
    switch (field) {
      case 'name':
        return ingredient.name.toLowerCase();
      case 'category':
        return (ingredient.categoryName || ingredient.category || '').toLowerCase();
      case 'unit':
        return ingredient.unitDisplayName || ingredient.unitName || ingredient.unit || '';
      case 'isActive':
        return ingredient.isActive ? 1 : 0;
      default:
        return ingredient.name;
    }
  };

  // Usar el hook reutilizable
  const tableData = useTableData<Ingredient, SortField>(
    {
      data: ingredients,
      defaultSortField: 'name',
      defaultSortDirection: 'asc',
      itemsPerPage: 15,
      sortValueExtractor
    },
    fieldLabels
  );

  const getCategoryColor = (category: string) => {
    const colors = {
      carnes: 'bg-red-50 text-red-600',
      panes: 'bg-yellow-50 text-yellow-600',
      vegetales: 'bg-green-50 text-green-600',
      lacteos: 'bg-blue-50 text-blue-600',
      salsas: 'bg-purple-50 text-purple-600',
      condimentos: 'bg-pink-50 text-pink-600',
      bebidas: 'bg-cyan-50 text-cyan-600',
      otros: 'bg-gray-50 text-gray-600'
    };
    return colors[category as keyof typeof colors] || colors.otros;
  };


  if (ingredients.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay insumos</h3>
        <p className="text-gray-500">No se encontraron insumos con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="name"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Insumo
              </SortableHeader>
              <SortableHeader
                field="category"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Categoría
              </SortableHeader>
              <SortableHeader
                field="unit"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Unidad de medida
              </SortableHeader>
              <SortableHeader
                field="isActive"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Estado
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((ingredient) => {
              return (
                <tr key={ingredient.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">
                      {ingredient.name}
                    </span>
                  </td>
                  
                  <td className="px-4 py-4">
                    <Badge
                      variant="outline"
                      className={`${getCategoryColor(ingredient.categoryName || ingredient.category)} border-0`}
                    >
                      {ingredient.categoryName || ingredient.category}
                    </Badge>
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900 font-medium">
                      {ingredient.unitDisplayName || ingredient.unitName || ingredient.unit}
                    </div>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <Badge className={ingredient.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {ingredient.isActive ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>
                  
                  <td className="px-4 py-4">
                    <div className="flex gap-2 justify-center flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(ingredient)}
                        className="text-xs"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onManagePrices(ingredient)}
                        className="text-xs"
                      >
                        <DollarSign className="w-3 h-3 mr-1" />
                        Precios
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onManageLocations(ingredient)}
                        className="text-xs"
                      >
                        <MapPin className="w-3 h-3 mr-1" />
                        Locales
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onManageSuppliers(ingredient)}
                        className="text-xs"
                      >
                        <Building className="w-3 h-3 mr-1" />
                        Proveedores
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onToggleActive(ingredient)}
                        className={`text-xs ${ingredient.isActive
                          ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                          : 'text-green-600 hover:text-green-700 hover:bg-green-50'}`}
                      >
                        {ingredient.isActive ? (
                          <>
                            <PowerOff className="w-3 h-3 mr-1" />
                            Desactivar
                          </>
                        ) : (
                          <>
                            <Power className="w-3 h-3 mr-1" />
                            Reactivar
                          </>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};